import { onBeforeUnmount, ref, type CSSProperties } from 'vue'

type UseBottomSheetSwipeCloseOptions = {
  threshold?: number
  clickMoveTolerance?: number
  /** Attach move/end listeners to document — for handle-only drag zones. */
  trackOnDocument?: boolean
}

export function useBottomSheetSwipeClose(
  onClose: () => void,
  options: UseBottomSheetSwipeCloseOptions = {},
) {
  const threshold = options.threshold ?? 50
  const clickMoveTolerance = options.clickMoveTolerance ?? 5
  const trackOnDocument = options.trackOnDocument ?? false

  const startY = ref<number | null>(null)
  const isSwiping = ref(false)
  const hasMoved = ref(false)
  const swipingStyle = ref<CSSProperties>({})

  function handleSwipeStart() {
    hasMoved.value = false
    isSwiping.value = true
    swipingStyle.value = {
      ...swipingStyle.value,
      userSelect: 'none',
      transition: '0s',
    }

    if (trackOnDocument) {
      attachDocumentListeners()
    }
  }

  function handleSwipeMove(clientY: number) {
    if (!isSwiping.value) return

    const deltaY = clientY - (startY.value ?? 0)
    if (Math.abs(deltaY) > clickMoveTolerance) {
      hasMoved.value = true
    }

    swipingStyle.value = {
      ...swipingStyle.value,
      transform: `translateY(${deltaY > 0 ? deltaY : 0}px)`,
    }
  }

  function handleSwipeEnd(clientY: number) {
    if (!isSwiping.value) return

    const delta = clientY - (startY.value ?? 0)
    swipingStyle.value = {
      ...swipingStyle.value,
      userSelect: '',
      transition: '',
    }

    if (delta > threshold) {
      onClose()
    } else {
      swipingStyle.value = {
        ...swipingStyle.value,
        transform: 'translateY(0)',
      }
    }

    if (trackOnDocument) {
      detachDocumentListeners()
    }

    setTimeout(() => {
      isSwiping.value = false
      startY.value = null
    }, 100)
  }

  function handleDocumentTouchMove(event: TouchEvent) {
    if (!isSwiping.value) return

    handleSwipeMove(event.touches[0]?.clientY ?? 0)

    if (hasMoved.value) {
      event.preventDefault()
    }
  }

  function handleDocumentTouchEnd(event: TouchEvent) {
    handleSwipeEnd(event.changedTouches[0]?.clientY ?? 0)
  }

  function handleDocumentMouseMove(event: MouseEvent) {
    handleSwipeMove(event.clientY)
  }

  function handleDocumentMouseUp(event: MouseEvent) {
    handleSwipeEnd(event.clientY)
  }

  function attachDocumentListeners() {
    if (!import.meta.client) return

    document.addEventListener('touchmove', handleDocumentTouchMove, { passive: false })
    document.addEventListener('touchend', handleDocumentTouchEnd)
    document.addEventListener('mousemove', handleDocumentMouseMove)
    document.addEventListener('mouseup', handleDocumentMouseUp)
  }

  function detachDocumentListeners() {
    if (!import.meta.client) return

    document.removeEventListener('touchmove', handleDocumentTouchMove)
    document.removeEventListener('touchend', handleDocumentTouchEnd)
    document.removeEventListener('mousemove', handleDocumentMouseMove)
    document.removeEventListener('mouseup', handleDocumentMouseUp)
  }

  function handleMouseDown(event: MouseEvent) {
    startY.value = event.clientY
    handleSwipeStart()
  }

  function handleMouseMove(event: MouseEvent) {
    handleSwipeMove(event.clientY)
  }

  function handleMouseUp(event: MouseEvent) {
    handleSwipeEnd(event.clientY)
  }

  function handleTouchStart(event: TouchEvent) {
    startY.value = event.touches[0]?.clientY ?? 0
    handleSwipeStart()
  }

  function handleTouchMove(event: TouchEvent) {
    handleSwipeMove(event.touches[0]?.clientY ?? 0)
  }

  function handleTouchEnd(event: TouchEvent) {
    handleSwipeEnd(event.changedTouches[0]?.clientY ?? 0)
  }

  function handleHandleClick() {
    if (!hasMoved.value) {
      onClose()
    }
  }

  onBeforeUnmount(() => {
    detachDocumentListeners()
  })

  return {
    swipingStyle,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleHandleClick,
  }
}
